// Shared client-side catalogue permission context (Module 01 §7).
//
// The server is the ONLY authorisation boundary — every mutation is authorised
// API-side and cost fields are redacted in the serialization layer. This hook
// exists so the UI does not OVER-SHOW controls a role cannot use (activate,
// discontinue, author, run/release cost, …): a hidden control is a convenience,
// never a security guarantee.
//
// It derives the caller's effective catalogue permission codes from their role
// codes (`/v1/auth/tenants` — the only caller-role signal the client exposes),
// mirroring the authoritative role→permission grants in
// `packages/contracts/src/permissions.ts` (`systemRoleGrants`, the catalogue.*
// slice). Custom (non-system) roles cannot be resolved from a role code alone,
// so they contribute no client-side grants and rely on the server gate; a hidden
// control for such a role is a graceful degradation, never a false allow. When a
// shared platform "effective permissions" feed lands, swap this local mirror for
// it (same note as `screens/uom-templates/caps.ts`).
//
// The role-feed plumbing (cache key + host→membership resolution) is shared with
// every other area's mirror in `lib/role-codes.ts`; the catalogue-specific part
// is the grant table below. The names re-exported here are the ones this area's
// screens and tests already import.

import {
  AUTH_TENANTS_QUERY_KEY,
  derivePermissions,
  useRoleCodesFeed,
  type AreaPermissions,
  type Membership,
  type RoleInput,
} from "../../../../lib/role-codes";

export { AUTH_TENANTS_QUERY_KEY, type Membership };

/**
 * Role code → catalogue.* permission codes. This is the catalogue slice of the
 * contracts `systemRoleGrants` (spec 01 §7). It MUST stay in step with that
 * table — the server enforces the real grants; this only governs what the UI
 * offers. Roles not present here (custom roles) resolve to no client grants.
 */
export const CATALOGUE_ROLE_GRANTS: Readonly<Record<string, readonly string[]>> = {
  group_admin: [
    "catalogue.read",
    "catalogue.structure.read",
    "catalogue.product.write",
    "catalogue.product.activate",
    "catalogue.product.discontinue",
    "catalogue.product.reactivate",
    "catalogue.bom.write",
    "catalogue.bom.activate",
    "catalogue.routing.write",
    "catalogue.routing.activate",
    "catalogue.uom.manage",
    "catalogue.schema.manage",
    "catalogue.import.run",
    "catalogue.template.manage",
    "catalogue.cost.read",
    "catalogue.cost.run",
    "catalogue.cost.release",
  ],
  facility_admin: [
    "catalogue.read",
    "catalogue.structure.read",
    "catalogue.product.write",
    "catalogue.product.activate",
    "catalogue.product.discontinue",
    "catalogue.bom.write",
    "catalogue.bom.activate",
    "catalogue.routing.write",
    "catalogue.routing.activate",
    "catalogue.uom.manage",
    "catalogue.template.manage",
    "catalogue.import.run",
    "catalogue.cost.read",
  ],
  // planner authors + activates products/BOMs/routings and runs imports; product
  // lifecycle is activate-only (no discontinue/reactivate — §3.1). cost.read is
  // grantable but off by default, so it is NOT in the seeded planner grant.
  planner: [
    "catalogue.read",
    "catalogue.structure.read",
    "catalogue.product.write",
    "catalogue.product.activate",
    "catalogue.bom.write",
    "catalogue.bom.activate",
    "catalogue.routing.write",
    "catalogue.routing.activate",
    "catalogue.import.run",
  ],
  sales: ["catalogue.read"],
  // procurement authors raw/consumable products only (kind-scoped server-side,
  // §7); the write grant is present, the kind scope is enforced by the API.
  procurement: ["catalogue.read", "catalogue.structure.read", "catalogue.product.write"],
  finance: [
    "catalogue.read",
    "catalogue.structure.read",
    "catalogue.cost.read",
    "catalogue.cost.run",
    "catalogue.cost.release",
  ],
  qc: ["catalogue.read", "catalogue.structure.read"],
  maintenance: ["catalogue.read"],
  storekeeper: ["catalogue.read"],
  floor_worker: ["catalogue.read"],
};

/**
 * The effective catalogue permission set (pure, unit-tested): the feed's own
 * permission list cut to this slice (D-586), or, for a role signal without
 * one, the union of every known role's grants (unknown roles add nothing).
 */
export function deriveCataloguePermissions(roles: RoleInput): Set<string> {
  return derivePermissions(roles, CATALOGUE_ROLE_GRANTS);
}

export type CataloguePermissions = AreaPermissions;

/**
 * The caller's catalogue permissions. `has` is fail-closed: it returns false
 * until the role feed settles and if the feed fails (screens degrade to
 * read-only / hide write controls rather than flashing a control a role lacks).
 */
export function useCataloguePermissions(): CataloguePermissions {
  const feed = useRoleCodesFeed();
  const codes = deriveCataloguePermissions(feed);
  return { isResolved: feed.isResolved, has: (code: string) => codes.has(code) };
}
